import { Component } from 'preact';
import { GOLD, TEXT_PRIMARY, TEXT_BODY, ACCENT_GOLD } from '../lib/medievalTheme';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { plainPreview } from './comments/CommentThread';
import { cn } from '../lib/utils';
import { t } from '../localization/t';

// A Tavern message forwarded into a DM, on the same plate as the replay
// and invite cards.
//
// This IS the chat bubble, not something drawn inside one: renderMessage
// hands `tavern` messages straight here, the way it does replays. The card
// is one click, back to the message where it sits in the Tavern, so it
// keeps the plate's hover lift and press.
//
// Everything it draws is the snapshot the server took at forward time
// (`metadata`: the author, the body, the timestamp), so a message edited or
// deleted since still reads as it was forwarded. The click carries the id,
// and the Tavern view scrolls to the live row or says it has gone.

const LABEL_SHADOW = '0 1px 2px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.85), 0 0 14px rgba(0,0,0,0.6)';
// Hearth light: a warm bloom low in the left corner over the panels' dark
// ground, with a second, fainter glow top right so the plate is lit from
// the room and not from one lamp.
const WASH = [
  'radial-gradient(ellipse 80% 110% at 12% 110%, rgba(196,124,44,0.62) 0%, rgba(96,58,20,0.45) 40%, transparent 72%)',
  'radial-gradient(ellipse 60% 80% at 100% 0%, rgba(212,168,67,0.22) 0%, transparent 60%)',
  'linear-gradient(180deg, #1a140c 0%, #100c07 100%)',
].join(', ');
const BODY_LINES = 4;

export default class TavernMessageCard extends Component {
  render() {
    const { meta, sorceryCards, onOpen, className } = this.props;
    const name = meta?.playerName || t('Someone');
    const avatarUrl = meta?.playerAvatar ? resolveAvatarUrl({ profileAvatar: meta.playerAvatar }, sorceryCards) : null;
    const when = meta?.createdAt ? new Date(meta.createdAt).toLocaleDateString() : null;

    return (
      <button
        type="button"
        className={cn('vh-mode-tile replay-card-in', className)}
        title={t('Open in the Tavern')}
        onClick={() => onOpen?.(meta?.messageId)}
      >
        <div className="vh-mode-tile-art" aria-hidden>
          {/* No photograph: the hub art's centrepiece blurs into a blot
              behind a quote. The plate is the hearth's light on grain,
              the material the search well and the panels already wear. */}
          <div className="absolute inset-0" style={{ background: WASH }} />
          <div className="absolute inset-0" style={{ background: 'url("/tex-noise.webp")', opacity: 0.35, mixBlendMode: 'overlay' }} />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(8,6,4,0.35) 0%, rgba(8,6,4,0.15) 50%, rgba(8,6,4,0.6) 100%)' }}
          />
          <div className="vh-mode-tile-ember" />
          <div className="vh-mode-tile-sheen" />
        </div>

        <span
          className="absolute inset-0 flex flex-col gap-1 text-left"
          style={{ padding: '0.625cqw 0.75cqw', zIndex: 4 }}
        >
          <span className="flex items-center gap-1 w-full min-w-0">
            <svg
              className="shrink-0"
              style={{ width: '0.8cqw', height: '0.8cqw', color: ACCENT_GOLD, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.95))' }}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z" />
              <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
            </svg>
            <span className="arena-heading text-[0.6875cqw] font-semibold" style={{ color: ACCENT_GOLD, textShadow: LABEL_SHADOW }}>
              {t('From the Tavern')}
            </span>
            {when ? (
              <span className="ml-auto text-[0.625cqw] tabular-nums" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
                {when}
              </span>
            ) : null}
          </span>

          <span className="flex items-center gap-1 min-w-0">
            <span
              className="shrink-0 overflow-hidden rounded-full"
              style={{ width: '1.125cqw', height: '1.125cqw', background: '#1a1611', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)' }}
            >
              {avatarUrl ? (
                <AvatarImage src={avatarUrl} className="w-full h-full" />
              ) : (
                <span className="flex w-full h-full items-center justify-center text-[0.6cqw] font-semibold" style={{ color: ACCENT_GOLD }}>
                  {name.slice(0, 1).toUpperCase()}
                </span>
              )}
            </span>
            <span className="truncate text-[0.6875cqw] font-semibold" style={{ color: TEXT_PRIMARY, textShadow: LABEL_SHADOW }}>
              {name}
            </span>
          </span>

          {/* The quote: a gold rule down its left, the way a reply reads
              in the Tavern, clamped so a long post keeps the plate's
              proportions and the footer stays on it. */}
          <span className="flex-1 min-h-0 flex items-center min-w-0">
            <span
              className="text-[0.75cqw] leading-snug"
              style={{
                color: TEXT_PRIMARY,
                textShadow: LABEL_SHADOW,
                paddingLeft: '0.5cqw',
                borderLeft: `2px solid ${GOLD} 0.6)`,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: BODY_LINES,
                overflow: 'hidden',
              }}
            >
              {plainPreview(meta?.body)}
            </span>
          </span>

          <span className="flex items-center gap-1 w-full">
            <svg
              className="shrink-0"
              style={{ width: '0.75cqw', height: '0.75cqw', color: ACCENT_GOLD, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.95))' }}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M7 17 17 7" />
              <path d="M8 7h9v9" />
            </svg>
            <span className="text-[0.6875cqw] font-medium" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
              {t('Open in the Tavern')}
            </span>
          </span>
        </span>
      </button>
    );
  }
}
